import React, { useContext, useEffect, useMemo, useState } from "react";
import "./ClientesDashboard.css";
import { Button, Col, Row } from "antd";
import axios from "axios";
import * as links from "../../../../utils/links";
import { AuthContext } from "../../../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import LoadingAction from "../../../../themes/LoadingAction/LoadingAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { format } from "date-fns";

const ClientesDashboard = () => {
  const { setDataUser, authInfo, setNotiMessage } = useContext(AuthContext);
  const { dataUser } = authInfo;
  const navigate = useNavigate();
  const token = authInfo?.dataUser?.token;

  const [totalClientes, setTotalClientes] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("TODOS");

  const headers = {
    "x-access-token": token,
    "content-type": "application/json",
  };

  const normalizarCliente = (cliente) => {
    const maquinasLegadas = Array.isArray(cliente?.Maquina) ? cliente.Maquina : [];
    const total = Number(cliente?.maquinas ?? maquinasLegadas.length ?? 0);
    const onlineCalculado = maquinasLegadas.filter((m) => {
      if (!m?.ultimaRequisicao) return false;
      return Date.now() - new Date(m.ultimaRequisicao).getTime() <= 60000;
    }).length;
    const online = Number(cliente?.online ?? onlineCalculado ?? 0);

    return {
      ...cliente,
      maquinas: total,
      online,
      offline: Number(cliente?.offline ?? Math.max(total - online, 0)),
    };
  };

  const carregarClientes = async () => {
    try {
      const resNova = await axios.get(
        `${process.env.REACT_APP_SERVIDOR}/admin-2027/clientes`,
        { headers }
      );
      return Array.isArray(resNova.data) ? resNova.data : [];
    } catch (err) {
      const resLegada = await axios.get(
        `${process.env.REACT_APP_SERVIDOR}/clientes`,
        { headers }
      );
      return Array.isArray(resLegada.data) ? resLegada.data : [];
    }
  };

  const carregarResumo = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVIDOR}/admin-2027/resumo`,
        { headers }
      );
      return res.data || null;
    } catch (_) {
      return null;
    }
  };

  const dataData = async () => {
    setIsLoading(true);
    try {
      const [clientesBrutos, resumoNovo] = await Promise.all([
        carregarClientes(),
        carregarResumo(),
      ]);

      const clientes = clientesBrutos
        .map(normalizarCliente)
        .sort((a, b) =>
          String(a?.nome || "").localeCompare(String(b?.nome || ""), "pt-BR", {
            sensitivity: "base",
          })
        );

      setTotalClientes(clientes);

      if (resumoNovo) {
        setResumo(resumoNovo);
      } else {
        const maquinas = clientes.reduce((s, c) => s + Number(c.maquinas || 0), 0);
        const online = clientes.reduce((s, c) => s + Number(c.online || 0), 0);
        setResumo({
          clientes: clientes.length,
          maquinas,
          online,
          offline: Math.max(maquinas - online, 0),
          atualizadoEm: new Date().toISOString(),
        });
      }
    } catch (err) {
      if ([401, 403].includes(err.response?.status)) {
        setNotiMessage({
          type: "error",
          message: "A sua sessão expirou, para continuar faça login novamente.",
        });
        navigate(links.SIGNIN);
        setDataUser(null);
      } else {
        setNotiMessage({
          type: "error",
          message: "Não foi possível atualizar os clientes agora.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dataData();
    const intervalId = setInterval(dataData, 60000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const situacaoCliente = (cliente) => {
    if (cliente?.isentoMensalidade) {
      return { texto: "ISENTO", classe: "isento", inadimplente: false };
    }
    if (!cliente?.dataVencimento) {
      return { texto: "SEM VENCIMENTO", classe: "sem-vencimento", inadimplente: false };
    }
    const vencimento = new Date(cliente.dataVencimento).getTime();
    if (!Number.isFinite(vencimento)) {
      return { texto: "SEM VENCIMENTO", classe: "sem-vencimento", inadimplente: false };
    }
    const dias = Math.floor((Date.now() - vencimento) / 86400000);
    const inadimplente = dias > 10;
    return {
      texto: inadimplente ? "INADIMPLENTE" : "REGULAR",
      classe: inadimplente ? "inadimplente" : "regular",
      inadimplente,
    };
  };

  const clientesFiltrados = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");

    return totalClientes.filter((cliente) => {
      const situacao = situacaoCliente(cliente);
      const bateBusca =
        !termo ||
        String(cliente?.nome || "").toLocaleLowerCase("pt-BR").includes(termo) ||
        String(cliente?.email || "").toLocaleLowerCase("pt-BR").includes(termo);

      if (!bateBusca) return false;
      if (filtro === "COM_ONLINE") return Number(cliente.online || 0) > 0;
      if (filtro === "TODAS_OFFLINE") return Number(cliente.maquinas || 0) > 0 && Number(cliente.online || 0) === 0;
      if (filtro === "REGULAR") return !situacao.inadimplente;
      if (filtro === "INADIMPLENTE") return situacao.inadimplente;
      return true;
    });
  }, [totalClientes, busca, filtro]);

  const formatarData = (valor, comHora = false) => {
    if (!valor) return "--";
    const dt = new Date(valor);
    if (!Number.isFinite(dt.getTime())) return "--";
    return format(dt, comHora ? "dd/MM/yyyy - HH:mm" : "dd/MM/yyyy");
  };

  const handleClienteClick = (post) => {
    navigate(`${links.CLIENTES_MAQUINAS}/${post.id}`, {
      state: {
        id: post.id,
        nome: post.nome,
        email: post.email,
        ativo: post.ativo,
        dataInclusao: post.dataInclusao,
        dataVencimento: post.dataVencimento,
        ultimoAcesso: post.ultimoAcesso,
        mercadoPagoToken: post?.mercadoPagoToken,
        pagbankToken: post?.pagbankToken ?? "",
        pagbankEmail: post?.pagbankEmail ?? "",
      },
    });
  };

  return (
    <div className="clientes-central">
      {isLoading && <LoadingAction />}

      <div className="Cliente_WarningMsgSpan">
        <span>{dataUser?.warningMsg}</span>
      </div>

      <div className="Cliente_Dashboard_header">
        <div>
          <div className="Cliente_Dashboard_staBlockTitle">Central de Telemetria</div>
          <div className="clientes-central-subtitulo">
            Visão operacional das empresas. Os valores Pix diário e mensal ficam dentro de cada empresa.
          </div>
        </div>
      </div>

      <div className="clientes-resumo-grid">
        <div className="clientes-resumo-card">
          <span>Empresas</span>
          <strong>{resumo?.clientes ?? totalClientes.length}</strong>
        </div>
        <div className="clientes-resumo-card">
          <span>Máquinas</span>
          <strong>{resumo?.maquinas ?? 0}</strong>
        </div>
        <div className="clientes-resumo-card resumo-online">
          <span>Online</span>
          <strong>{resumo?.online ?? 0}</strong>
        </div>
        <div className="clientes-resumo-card resumo-offline">
          <span>Offline</span>
          <strong>{resumo?.offline ?? 0}</strong>
        </div>
      </div>

      <div className="Cliente_Dashboard_action">
        <Button onClick={dataData}>
          <FontAwesomeIcon icon={faArrowsRotate} style={{ marginRight: "5px" }} />
          Atualizar
        </Button>
        <Link to={links.ADD_CLIENTES}>
          <Button className="Cliente_Dashboard_addbtn">
            <AiOutlinePlusCircle />
            <span>Criar novo cliente</span>
          </Button>
        </Link>
      </div>

      <div className="clientes-filtros">
        <div className="clientes-busca">
          <i className="bi bi-search" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar empresa ou e-mail"
            aria-label="Buscar empresa ou e-mail"
          />
        </div>
        <select value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Filtrar clientes">
          <option value="TODOS">Todas as empresas</option>
          <option value="COM_ONLINE">Com máquina online</option>
          <option value="TODAS_OFFLINE">Todas as máquinas offline</option>
          <option value="REGULAR">Regular / isento</option>
          <option value="INADIMPLENTE">Inadimplente</option>
        </select>
        <div className="clientes-resultado-contagem">
          {clientesFiltrados.length} de {totalClientes.length}
        </div>
      </div>

      <Row>
        {clientesFiltrados.map((post) => {
          const situacao = situacaoCliente(post);
          return (
            <Col
              key={post.id}
              xs={24}
              md={20}
              lg={20}
              xl={20}
              className="Cliente_Dashboard_col"
            >
              <div
                className="Cliente"
                onClick={() => handleClienteClick(post)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClienteClick(post); }}
                role="button"
                tabIndex={0}
              >
                <div className="Cliente-info">
                  <div className="cliente-identificacao">
                    <h3>{post.nome}</h3>
                    <div className="Cliente-email">{post.email}</div>
                    <div className="cliente-maquinas-status">
                      <span>Total: <b>{post.maquinas || 0}</b></span>
                      <span className="cliente-online">Online: <b>{post.online || 0}</b></span>
                      <span className="cliente-offline">Offline: <b>{post.offline || 0}</b></span>
                    </div>
                  </div>
                  <div className="Cliente-type-info">
                    <div className="Cliente-type-title">
                      <div>Situação:</div>
                      <div>Vencimento:</div>
                    </div>
                    <div>
                      <div className={`Cliente-type-val ${situacao.classe}`}>{situacao.texto}</div>
                      <div>{formatarData(post.dataVencimento)}</div>
                    </div>
                  </div>
                </div>
                <div className="Cliente-footer">
                  <div>Inclusão: {formatarData(post.dataInclusao, true)}</div>
                  <div>Último acesso: {formatarData(post.ultimoAcesso, true)}</div>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      {!isLoading && clientesFiltrados.length === 0 && (
        <div className="clientes-vazio">Nenhuma empresa encontrada com esse filtro.</div>
      )}
    </div>
  );
};

export default ClientesDashboard;
